import {di, lazyGet} from "@dp-instant/core";
import {inject, injectable} from "inversify";
import {PlayerType} from "@game/coregame/interfaces/PlayerType";
import {TypesGame} from "@game/TypesGame";
import {GameModel} from "@game/coregame/model/GameModel";
import {CookieModel} from "@game/coregame/model/CookieModel";

type color = string;
export type PlayerColor = [fill: color, unit: color, base?: color];
type AimColor = [fill: color, alpha: number];

type GameColorsSetting = {
    players: {[key in PlayerType]: PlayerColor};
    aims: {
        allied: AimColor;
        enemy: AimColor;
    };
};

const defaultBlueSet: PlayerColor = ["#4BB4F8", "#33698D"];
const defaultRedSet: PlayerColor = ["#F53514", "#77160D"];

export const GameColors: GameColorsSetting = {
    players: {
        [PlayerType.Default]: ["#4BB4F8", "#33698D"],
        [PlayerType.First]: defaultBlueSet,
        [PlayerType.Second]: defaultRedSet,
        [PlayerType.Third]: ["#59D267", "#1E7824"],
        [PlayerType.Fourth]: ["#A563F2", "#501E84"],
        [PlayerType.Fifth]: ["#EE3B82", "#680443"],
        [PlayerType.Sixth]: ["#30CFAF", "#066056"],
        [PlayerType.Seventh]: ["#F8AE20", "#895E0B"],
        [PlayerType.Eighth]: ["#BAAB2A", "#323B0E"],
        [PlayerType.Nine]: ["#DC6CC5", "#5F297D"],
        [PlayerType.Neutral]: ["#A2A2A2", "#1C1C1C", "#ffffff"]
    },
    aims: {
        allied: ["#4ab4f9", 0.298],
        enemy: ["#f43514", 0.298]
    }
};

// Selectable

export const enum SkinType {
    BUILDING = "SkinType.BUILDING",
    FIGHTER = "SkinType.FIGHTER",
    COLOR = "SkinType.COLOR"
}
export type GiftType = {
    id: number;
    type: SkinType.BUILDING | SkinType.FIGHTER | "money";
    value?: number;
};

const buildingTexturePrefix: string = "buildings/";
const fighterTexturePrefix: string = "fighters/";
const selectable_items_ext: string = ".svg";

const textureUrlHelper = (id: number, prefix: string): string =>
    prefix + id.toString() + selectable_items_ext;

export const enum UserSelectableColorsSet {
    BLUE = "UserSelectableColorsSet.BLUE",
    RED = "UserSelectableColorsSet.RED"
}

type SelectableItemData = {
    id: number;
    type: SkinType;
    playerLevelUnlock: number;
    isAvailable?: boolean;
    enemyLevelUnlock: number;

    textureUrl: string;
    ui_textureUrl: string;

    coinsPrice?: number;
};

type SelectableColorData = {
    id: UserSelectableColorsSet;
    data: PlayerColor;
};

export const SelectableColorCss: {id: UserSelectableColorsSet; className?: string}[] = [
    {
        id: UserSelectableColorsSet.BLUE,
        className: "skins-blue-color-set"
    },
    {
        id: UserSelectableColorsSet.RED,
        className: "skins-red-color-set"
    }
];

export const SelectableColorsDataSet: SelectableColorData[] = [
    {
        id: UserSelectableColorsSet.BLUE,
        data: defaultBlueSet
    },
    {
        id: UserSelectableColorsSet.RED,
        data: defaultRedSet
    }
];

type UserStorableItem = {
    stored?: boolean | undefined;
};

type UserSelectableItem = {
    selected: boolean;
};

export interface ISelectableBuilding
    extends UserStorableItem,
        UserSelectableItem,
        SelectableItemData {}

export interface ISelectableFighter
    extends UserStorableItem,
        UserSelectableItem,
        SelectableItemData {}

export interface ISelectableColor
    extends UserStorableItem,
        UserSelectableItem,
        SelectableColorData {}

export const SelectableBuildingDataSet: SelectableItemData[] = [
    {id: 1, enemyLevelUnlock: 1, playerLevelUnlock: 1, coinPrice: 5000},
    {id: 2, enemyLevelUnlock: 8, playerLevelUnlock: 1, coinsPrice: 5000},
    {id: 3, enemyLevelUnlock: 8, playerLevelUnlock: 1, coinsPrice: 5000},
    {id: 4, enemyLevelUnlock: 8, playerLevelUnlock: 1, coinsPrice: 5000},
    {id: 5, enemyLevelUnlock: 8, playerLevelUnlock: 1, coinsPrice: 5000},
    {id: 6, enemyLevelUnlock: 8, playerLevelUnlock: 1, coinsPrice: 5000},
    {id: 7, enemyLevelUnlock: 8, playerLevelUnlock: 1, coinsPrice: 5000},
    {id: 8, enemyLevelUnlock: 8, playerLevelUnlock: 1, coinsPrice: 5000},
    {id: 9, enemyLevelUnlock: 8, playerLevelUnlock: 1, coinsPrice: 5000},
    {id: 10, enemyLevelUnlock: 8, playerLevelUnlock: 1, coinsPrice: 5000},
    {id: 11, enemyLevelUnlock: 10, playerLevelUnlock: 1, coinsPrice: 5000},
    {id: 12, enemyLevelUnlock: 10, playerLevelUnlock: 1, coinsPrice: 5000},
    {id: 13, enemyLevelUnlock: 10, playerLevelUnlock: 1, coinsPrice: 5000},
    {id: 14, enemyLevelUnlock: 12, playerLevelUnlock: 1, coinsPrice: 5000},
    {id: 15, enemyLevelUnlock: 12, playerLevelUnlock: 1, coinsPrice: 5000},
    {id: 16, enemyLevelUnlock: 12, playerLevelUnlock: 1, coinsPrice: 5000},
    {id: 17, enemyLevelUnlock: 14, playerLevelUnlock: 1, coinsPrice: 5000},
    {id: 18, enemyLevelUnlock: 14, playerLevelUnlock: 1, coinsPrice: 5000},
    {id: 19, enemyLevelUnlock: 14, playerLevelUnlock: 10, coinsPrice: 10000},
    {id: 20, enemyLevelUnlock: 16, playerLevelUnlock: 10, coinsPrice: 10000},
    {id: 21, enemyLevelUnlock: 16, playerLevelUnlock: 10, coinsPrice: 10000},
    {id: 22, enemyLevelUnlock: 16, playerLevelUnlock: 14, coinsPrice: 12000},
    {id: 23, enemyLevelUnlock: 18, playerLevelUnlock: 14, coinsPrice: 12000},
    {id: 24, enemyLevelUnlock: 18, playerLevelUnlock: 14, coinsPrice: 12000},
    {id: 25, enemyLevelUnlock: 18, playerLevelUnlock: 18, coinsPrice: 15000},
    {id: 26, enemyLevelUnlock: 20, playerLevelUnlock: 18, coinsPrice: 15000},
    {id: 27, enemyLevelUnlock: 20, playerLevelUnlock: 18, coinsPrice: 15000},
    {id: 28, enemyLevelUnlock: 20, playerLevelUnlock: 20, coinsPrice: 17500},
    {id: 29, enemyLevelUnlock: 22, playerLevelUnlock: 20, coinsPrice: 17500},
    {id: 30, enemyLevelUnlock: 22, playerLevelUnlock: 20, coinsPrice: 17500},
    {id: 31, enemyLevelUnlock: 22, playerLevelUnlock: 24, coinsPrice: 17500},
    {id: 32, enemyLevelUnlock: 24, playerLevelUnlock: 24, coinsPrice: 17500},
    {id: 33, enemyLevelUnlock: 24, playerLevelUnlock: 24, coinsPrice: 17500}
].map(item => ({
    ...item,
    type: SkinType.BUILDING,
    textureUrl: textureUrlHelper(item.id, "buildings/"),
    ui_textureUrl: textureUrlHelper(item.id, "buildings-ui/")
}));

export const SelectableFighterDataSet: SelectableItemData[] = [
    {id: 1, enemyLevelUnlock: 1, playerLevelUnlock: 1, coinsPrice: 5000},
    {id: 2, enemyLevelUnlock: 8, playerLevelUnlock: 1, coinsPrice: 5000},
    {id: 3, enemyLevelUnlock: 8, playerLevelUnlock: 1, coinsPrice: 5000},
    {id: 4, enemyLevelUnlock: 8, playerLevelUnlock: 1, coinsPrice: 5000},
    {id: 5, enemyLevelUnlock: 8, playerLevelUnlock: 1, coinsPrice: 5000},
    {id: 6, enemyLevelUnlock: 8, playerLevelUnlock: 1, coinsPrice: 5000},
    {id: 7, enemyLevelUnlock: 8, playerLevelUnlock: 1, coinsPrice: 5000},
    {id: 8, enemyLevelUnlock: 8, playerLevelUnlock: 1, coinsPrice: 5000},
    {id: 9, enemyLevelUnlock: 8, playerLevelUnlock: 1, coinsPrice: 5000},
    {id: 10, enemyLevelUnlock: 8, playerLevelUnlock: 1, coinsPrice: 5000},
    {id: 11, enemyLevelUnlock: 10, playerLevelUnlock: 1, coinsPrice: 5000},
    {id: 12, enemyLevelUnlock: 10, playerLevelUnlock: 1, coinsPrice: 5000},
    {id: 13, enemyLevelUnlock: 10, playerLevelUnlock: 1, coinsPrice: 5000},
    {id: 14, enemyLevelUnlock: 12, playerLevelUnlock: 1, coinsPrice: 5000},
    {id: 15, enemyLevelUnlock: 12, playerLevelUnlock: 1, coinsPrice: 5000},
    {id: 16, enemyLevelUnlock: 12, playerLevelUnlock: 1, coinsPrice: 5000},
    {id: 17, enemyLevelUnlock: 14, playerLevelUnlock: 1, coinsPrice: 5000},
    {id: 18, enemyLevelUnlock: 14, playerLevelUnlock: 1, coinsPrice: 5000},
    {id: 19, enemyLevelUnlock: 14, playerLevelUnlock: 4, coinsPrice: 10000},
    {id: 20, enemyLevelUnlock: 16, playerLevelUnlock: 4, coinsPrice: 10000},
    {id: 21, enemyLevelUnlock: 16, playerLevelUnlock: 4, coinsPrice: 10000},
    {id: 22, enemyLevelUnlock: 16, playerLevelUnlock: 12, coinsPrice: 12000},
    {id: 23, enemyLevelUnlock: 18, playerLevelUnlock: 12, coinsPrice: 12000},
    {id: 24, enemyLevelUnlock: 18, playerLevelUnlock: 12, coinsPrice: 12000},
    {id: 25, enemyLevelUnlock: 18, playerLevelUnlock: 18, coinsPrice: 15000},
    {id: 26, enemyLevelUnlock: 20, playerLevelUnlock: 18, coinsPrice: 15000},
    {id: 27, enemyLevelUnlock: 20, playerLevelUnlock: 18, coinsPrice: 15000},
    {id: 28, enemyLevelUnlock: 20, playerLevelUnlock: 30, coinsPrice: 17500},
    {id: 29, enemyLevelUnlock: 30, playerLevelUnlock: 30, coinsPrice: 17500},
    {id: 30, enemyLevelUnlock: 40, playerLevelUnlock: 30, coinsPrice: 17500}
].map(item => ({
    ...item,
    type: SkinType.FIGHTER,
    textureUrl: textureUrlHelper(item.id, "fighters/"),
    ui_textureUrl: textureUrlHelper(item.id, "fighters-ui/")
}));

/// Fighter speed presets

/**
 * @todo Refactor it
 * @description It's need to refactor according DI approach as soon as yotube and wechat will pass review
 * @author Alexander Slobodyan
 * @date 14 aug 2023
 */

const enum FightersSpeedSet {
    default = "FightersSpeedSet.default",
    wechat = "FightersSpeedSet.wechat",
    facebook = "FightersSpeedSet.facebook",
    youtube = "FightersSpeedSet.youtube",
    yandex = "FightersSpeedSet.yandex"
}

const currentPreset =
    __SOCIAL__ === "fb"
        ? FightersSpeedSet.facebook
        : __SOCIAL__ === "we"
        ? FightersSpeedSet.wechat
        : __SOCIAL__ === "yt" || __SOCIAL__ === "ls"
        ? FightersSpeedSet.youtube
        : __SOCIAL__ === "ya"
        ? FightersSpeedSet.yandex
        : FightersSpeedSet.default;

type FightersSpeedPreset = {
    DEFAULT_SIZE: number;
    NORMAL_SPEED: number;
    BURST_WAVES_LEN: number;
    BURST_DELAY: number;
};

/**
 * Base settings
 */

const defaultFigtersSpeedPreset: FightersSpeedPreset = {
    DEFAULT_SIZE: 7,
    NORMAL_SPEED: 9.375,
    BURST_WAVES_LEN: 0.75,
    BURST_DELAY: 0.3
};

/**
 * Facebook settings. It's copy of wechat settings.
 * It was separated in case of test.
 */

const facebookFigtersSpeedPreset: FightersSpeedPreset = {
    DEFAULT_SIZE: 9,
    NORMAL_SPEED: 13,
    BURST_WAVES_LEN: 0.87,
    BURST_DELAY: 0.2
};

const wechatFigtersSpeedPreset: FightersSpeedPreset = {
    DEFAULT_SIZE: 9,
    NORMAL_SPEED: 13,
    BURST_WAVES_LEN: 0.87,
    BURST_DELAY: 0.2
};

const youtubeFigtersSpeedPreset: FightersSpeedPreset = {
    DEFAULT_SIZE: 9,
    NORMAL_SPEED: 13,
    BURST_WAVES_LEN: 0.87,
    BURST_DELAY: 0.2
};
const yandexFigtersSpeedPreset: FightersSpeedPreset = {
    DEFAULT_SIZE: 9,
    NORMAL_SPEED: 13,
    BURST_WAVES_LEN: 0.87,
    BURST_DELAY: 0.2
};

export const getFightersSpeedPreset = (preset = currentPreset) => {
    switch (preset) {
        case FightersSpeedSet.default:
            return defaultFigtersSpeedPreset;
            break;
        case FightersSpeedSet.wechat:
            return wechatFigtersSpeedPreset;
            break;
        case FightersSpeedSet.facebook:
            return facebookFigtersSpeedPreset;
            break;
        case FightersSpeedSet.youtube:
            return youtubeFigtersSpeedPreset;
            break;
        case FightersSpeedSet.yandex:
            return yandexFigtersSpeedPreset;
            break;
        default:
            return defaultFigtersSpeedPreset;
    }
};
